import { useLayoutEffect, useRef, useState } from 'react';
import {
	DynamicFormStateType,
	InitializeObjectType,
	PageDescriptionType,
	useDynamicFormHandlerType,
} from '.';
import ScriptMediator from './ScriptEngine';
import { ClientJsonParser } from './ScriptEngine/Utils/ClientJsonParser';
import buildViewModel, {
	FormViewModel,
	PageContainerViewModel,
} from './Models';
import BaseViewModel from './Models/BaseViewModel';
import ViewModelDataCollector from './ScriptEngine/ViewModelDataCollector';
import { RubyControl } from 'src/Services';

const initState = {
	loaded: false,
	data: {},
	pages: [],
	fileInfos: [],
};

const useDynamicForm: useDynamicFormHandlerType = () => {
	const mediator = useRef<ScriptMediator>();
	const [formState, setFrmState] = useState<DynamicFormStateType>(initState);
	const [isOpalReady, setIsOpalReady] = useState<boolean>(
		!!(window as any).Opal,
	);

	/** При прогрузки ДОМ-дерева, грузим Руби-скрипты */
	useLayoutEffect(() => {
		RubyControl.initRuby();
		if (!(window as any).Opal) {
			const handleOpalReady = () => {
				setIsOpalReady(true);
				if (mediator.current?.viewModel) {
					const savedPageIndex = mediator.current.currentPageIndex;
					mediator.current.currentPageIndex = null;
					mediator.current.isInitializing = true;
					mediator.current.runDefaultScripts(
						mediator.current.viewModel,
					);
					mediator.current.isInitializing = false;
					mediator.current.currentPageIndex = savedPageIndex;
				}
			};
			window.addEventListener('opal-ready', handleOpalReady, {
				once: true,
			});
			return () => {
				window.removeEventListener('opal-ready', handleOpalReady);
				RubyControl.unInitRuby();
			};
		}
		return () => {
			RubyControl.unInitRuby();
		};
	}, []);

	/** Подготовка данных данных */
	const prepareData = (
		param: InitializeObjectType,
		mediator: ScriptMediator,
	) => {
		try {
			const formContextData = JSON.parse(
				param?.formContextData ?? '{}',
				ClientJsonParser.parse,
			);
			const dataContext = JSON.parse(
				param.dataContext,
				ClientJsonParser.parse,
			);
			console.timeLog('InitDynamicForm', 'Парсинг JSON');
			BaseViewModel.mediator = mediator;
			BaseViewModel.formContextData = formContextData;
			BaseViewModel.dataContext = dataContext;
			const sizesRaw = param.attachmentsWithSizes ?? {};
			const normalized: Record<string, number> = {};
			Object.keys(sizesRaw).forEach((k) => {
				const v = sizesRaw[k];
				const n = typeof v === 'number' ? v : parseInt(String(v), 10);
				if (Number.isFinite(n) && n > 0) {
					normalized[k] = n;
				}
			});
			BaseViewModel.attachmentsWithSizes = normalized;
			BaseViewModel.fileId = param.fileId;
			BaseViewModel.componentId = param.componentId;
			BaseViewModel.typename = param.typename;
			BaseViewModel.EntityPathToViewModel = {};
			BaseViewModel.scanSettings = param.scanSettings;
			mediator.onSetPropertyValue = onSetPropertyValue;
			// console.log( "formContextData",  formContextData );
			// console.log( "dataContext",  dataContext );
			const model = buildViewModel(param.formDescription, dataContext);
			console.timeLog('InitDynamicForm', 'Построение модели');
			// при наличии шагов запускаем скрипты только на текущей странице
			if (model?.FormElements?.[0]?.ControlKey === 'PageContainer') {
				mediator.setCurrentPageIndex(0);
			}
			mediator.onViewModelBuilded(model);
			// инициализируем видимость шагов по DisplayScript для всех страниц
			if (typeof mediator.initPagesVisibility === 'function') {
				mediator.initPagesVisibility();
			}
			return model;
		} catch (error: any) {
			console.warn(error);
		}
	};

	const initForm = (param: InitializeObjectType, readOnly: boolean) => {
		return new Promise<string>((resolve, reject) => {
			console.time('InitDynamicForm');
			mediator.current = new ScriptMediator(readOnly);
			console.timeLog('InitDynamicForm', 'Инициация медиатора');
			const model = prepareData(param, mediator.current);
			console.timeLog('InitDynamicForm', 'Обработка модули медиатором');

			if (!model) {
				throw Error('Ошибка при генерации модели данных');
			}

			// собираем initialValues для Formik из построенной модели
			const initialData =
				ViewModelDataCollector.collectFormDataKoVm(model);
			const formValues = checkIsPages(model)
				? (initialData as Record<string, unknown>)['_']
				: initialData;

			setFrmState((prev) => {
				return {
					...prev,
					rootModel: model,
					data: formValues || {},
					pages: getPagesDescr(model),
					currentPage: 0,
					loaded: true,
				};
			});
			console.timeEnd('InitDynamicForm');
			resolve('success');
		});
	};

	const checkIsPages = (model: FormViewModel) => {
		if (!model) {
			return false;
		}
		return model.FormElements[0].ControlKey === 'PageContainer';
	};

	const getPagesDescr = (
		model: FormViewModel,
	): Array<PageDescriptionType> => {
		const pages: Array<PageDescriptionType> = [];
		if (checkIsPages(model)) {
			const pageContainer = model
				.FormElements[0] as PageContainerViewModel;
			for (let index = 0; index < pageContainer.Pages.length; index++) {
				const page = pageContainer.Pages[index];
				pages.push({
					index,
					title: page.Caption,
					status: page.IsControlVisible() ? '' : 'hidden',
				});
			}
		}
		return pages;
	};

	/** Необходима фиксировать изменения состояния вызванных в скриптах... */
	const onSetPropertyValue = (
		element: BaseViewModel,
		propertyName: string,
		value: any,
	) => {
		setFrmState((prev) => {
			// Пересчитываем видимость страниц, но не затираем уже выставленный статус валидации(error/complete),
			// иначе он сбрасывается на "" при любом изменении значения поля
			const freshPages = getPagesDescr(
				element.getRootElement() as FormViewModel,
			);
			const _pages = freshPages.map((page, index) => {
				const prevStatus = prev.pages[index]?.status;
				if (
					page.status !== 'hidden' &&
					!!prevStatus &&
					prevStatus !== 'hidden'
				) {
					return { ...page, status: prevStatus };
				}
				return page;
			});
			return {
				...prev,
				pages: _pages,
				data: {
					...prev.data,
					[element.Path]: {
						...prev.data[element.Path],
						[propertyName]: value,
					},
				},
			};
		});
	};

	const resetForm = () => {
		setFrmState(initState);
		ViewModelDataCollector.reset();
	};

	const formValidate = (pageIndex = 0, validateAll = false) => {
		if (checkIsPages(formState.rootModel!)) {
			const pageContainer = formState.rootModel
				?.FormElements[0] as PageContainerViewModel;
			const pageError = pageContainer.Pages[pageIndex].Validate();
			if (!pageError) {
				setFrmState((prev) => {
					const newState = { ...prev };
					newState.pages[pageIndex].status = 'error';
					return newState;
				});
			} else {
				setFrmState((prev) => {
					const newState = { ...prev };
					newState.pages[pageIndex].status = 'complete';
					return newState;
				});
			}
			if (validateAll) {
				return !!formState.rootModel?.Validate();
			}
			return pageError;
		} else {
			return !!formState.rootModel?.Validate();
		}
	};

	// Сбор данных с модели формы для отправки на сервер
	const collectFormDataKoVm = (withSimpleData: boolean = false) => {
		if (!formState.rootModel) {
			return null;
		}
		// Принудительно перезапускаем DefaultValueScript для всего дерева.
		// currentPageIndex = null отключает фильтр по PageIndex — обрабатываются все элементы,
		// включая скрытые поля и элементы вне PageContainer.
		// RunDefaultValueInitializationScript заполняет только пустые поля, пользовательский ввод не затрагивается.
		if (mediator.current) {
			const savedPageIndex = mediator.current.currentPageIndex;
			mediator.current.currentPageIndex = null;
			mediator.current.isInitializing = true;
			mediator.current.runDefaultScripts(formState.rootModel);
			mediator.current.isInitializing = false;
			mediator.current.currentPageIndex = savedPageIndex;
		}
		// Рекурсивно обрабатываем элементы, начиная с родительской главной формы
		const finalData = ViewModelDataCollector.collectFormDataKoVm(
			formState.rootModel,
		);
		// console.log( formState.rootModel, "result", finalData );
		//
		const simpleData = withSimpleData
			? ViewModelDataCollector.collectCurrentElementSimpleFormatDataFromKoVm(
					formState.rootModel,
				)
			: null;
		return {
			fileInfos: ViewModelDataCollector.fileInfos,
			formAttachments: ViewModelDataCollector.formAttachments,
			// TODO
			values:
				!formState.rootModel.FormElements ||
				!(formState.rootModel.FormElements[0] as any).Pages
					? finalData
					: finalData['_'],
			simpleData,
		};
	};

	return {
		initForm,
		formState,
		formValidate,
		resetForm,
		collectFormDataKoVm,
		isOpalReady,
	} as const;
};

export default useDynamicForm;
